import { SearchLogic } from '@tendium/prom-types';
import { IApiTenderHighlight, ITenderHighlight, ITenderKeywordHighlight } from './types';
import { capitalizeFirstLetter } from 'src/helpers/capitalizeFirstLetter';
import { HighlightColor, MpKeyword, MpNewKeyword } from 'src/models/matchingProfiles/types';

const MATCH_SIGN_REG = /<em>((.|\s)*?)<\/em>/g; // blank characters can be included inside <em>...</em> in the freya result
const REPLACE_SIGN_REG = /<em>|<\/em>/g;
const SPECIAL_CHAR = '[^\\wåäöÅÄÖÆØæøšŠžŽ]';
const HIGHLIGHT_KEYWORDS_SORT_ORDER = [HighlightColor.Green, HighlightColor.Yellow, HighlightColor.Red];

// Only reason we need this is:
// - special characters will be dropped in the freya result when it is at the beginning or in the end
function parseKeyword(keyword: string): string {
  return keyword.replace(new RegExp('^' + SPECIAL_CHAR + '+'), '').replace(new RegExp(SPECIAL_CHAR + '+$'), '');
}

function isKeywordInText(keyword: string, text: string, searchLogic?: SearchLogic): boolean {
  const parsedKeyword = parseKeyword(keyword);
  const wordStrReg = searchLogic === SearchLogic.PrefixMatch ? parsedKeyword + '.*' : parsedKeyword;
  try {
    const regexp = new RegExp('^(\\s?' + wordStrReg + '\\s?)+$', 'i');
    return !!keyword && regexp.test(text);
  } catch (e) {
    return false;
  }
}

function parseHighlightValues(values: string[]): string {
  return (values.join(' ').match(MATCH_SIGN_REG) || [])
    .map(w => w.replace(REPLACE_SIGN_REG, '').replace(/\s+/g, ' '))
    .join(' ');
}

export function groupByKeywords(keywordsRaw: MpNewKeyword[], highlightsRaw: IApiTenderHighlight[]): ITenderHighlight[] {
  if (!keywordsRaw.length) {
    return [];
  }
  const keywordsHighlights: ITenderHighlight[] = [];
  const sortedKeywords = [...keywordsRaw].sort((a, b) => {
    return a.value.length > b.value.length ? -1 : 1;
  });

  let restHighlights = [...highlightsRaw];
  const unrecognisedHighlights: ITenderKeywordHighlight[] = [];

  sortedKeywords.forEach(({ value, highlightColor, searchLogic }) => {
    const highlights: ITenderKeywordHighlight[] = [];
    const matchedIndexes: number[] = [];

    restHighlights.forEach(({ context, values, targetPath }, index) => {
      if (!!context) {
        const text = parseHighlightValues(values);

        if (isKeywordInText(value, text, searchLogic)) {
          matchedIndexes.push(index);
          values.forEach(value => {
            highlights.push({
              color: highlightColor,
              context,
              targetPath,
              value: value.replace(REPLACE_SIGN_REG, '**').replace(/\s+/g, ' ')
            });
          }, false);
        }
      }
    });

    restHighlights = restHighlights.filter((hl, i) => !matchedIndexes.includes(i));

    if (!!highlights.length) {
      keywordsHighlights.push({ keyword: capitalizeFirstLetter(value), highlights: [...new Set(highlights)] });
    }
  });

  restHighlights.forEach(({ context, values, targetPath }) => {
    context &&
      values.forEach(value => {
        unrecognisedHighlights.push({
          color: HighlightColor.Yellow,
          context,
          targetPath,
          value: value.replace(REPLACE_SIGN_REG, '**').replace(/\s+/g, ' ')
        });
      }, false);
  });

  if (!!unrecognisedHighlights.length) {
    keywordsHighlights.push({ keyword: '', highlights: [...new Set(unrecognisedHighlights)] });
  }

  return keywordsHighlights || [];
}

export function findMatchedKeywords(
  highlights?: IApiTenderHighlight[],
  keywords?: MpKeyword[]
): MpKeyword[] | undefined {
  if (!highlights?.length || !keywords?.length) return [];

  const flattenHighlights = highlights.flatMap(highlight => parseHighlightValues(highlight.values)) ?? [];

  const matchedKeywords = keywords.filter(keyword => {
    const parsedKeyword = parseKeyword(keyword.value);
    const wordStrReg = keyword.searchLogic === SearchLogic.PrefixMatch ? parsedKeyword + '.*' : parsedKeyword;
    try {
      // (?:^|\\s)${wordStrReg}(?:$|\\s) - set a word boundary that is either start/end of word or a whitespace
      // this ensures that we don't capture substrings and we can handle non-ascii chars
      const regexPattern =
        keyword.searchLogic === SearchLogic.ExactMatch
          ? new RegExp(`(?:^|\\s)${wordStrReg}(?:$|\\s)`, 'i')
          : new RegExp(`^${wordStrReg}`, 'i');
      return flattenHighlights.some(highlightValue => regexPattern.test(highlightValue));
    } catch (e) {
      return false;
    }
  });

  return matchedKeywords.sort((a, b) => {
    const keywordA = a.highlightColor ? HIGHLIGHT_KEYWORDS_SORT_ORDER.indexOf(a.highlightColor) : -1;
    const keywordB = b.highlightColor ? HIGHLIGHT_KEYWORDS_SORT_ORDER.indexOf(b.highlightColor) : -1;

    return keywordA - keywordB;
  });
}
